<template>
  <v-container fluid class="pa-5">
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <div class="d-flex justify-space-between align-center mb-4">
      <h1 class="text-h4">Executive Dashboard</h1>
      <div class="d-flex align-center">
        <v-select
          v-model="selectedYear"
          :items="years"
          label="Period"
          outlined
          dense
          hide-details
          class="mr-4"
          style="width: 150px"
          @change="fetchStats"
        ></v-select>
        <v-btn color="primary" @click="openReportDialog">
          <v-icon left>mdi-download</v-icon>
          Export Report
        </v-btn>
      </div>
    </div>

    <!-- Summary Cards -->
    <v-row>
      <v-col cols="12" sm="6" md="2">
        <v-card class="pa-4" outlined>
          <div class="text-overline mb-1">Total Applications</div>
          <div class="text-h4 font-weight-bold">{{ totalApplications }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <v-card class="pa-4" outlined color="success" dark>
          <div class="text-overline mb-1">Approved</div>
          <div class="text-h4 font-weight-bold">{{ approvedCount }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <v-card class="pa-4" outlined color="info" dark>
          <div class="text-overline mb-1">Approval Rate</div>
          <div class="text-h4 font-weight-bold">{{ approvalRate }}%</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <v-card class="pa-4" outlined color="warning" dark>
          <div class="text-overline mb-1">Pending Review</div>
          <div class="text-h4 font-weight-bold">{{ pendingCount }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <v-card class="pa-4" outlined color="error" dark>
          <div class="text-overline mb-1">Rejected/Returned</div>
          <div class="text-h4 font-weight-bold">{{ rejectedCount }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <v-card class="pa-4" outlined color="teal" dark>
          <div class="text-overline mb-1">Digital Seals Issued</div>
          <div class="text-h4 font-weight-bold">{{ sealedCount }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts Row 1 -->
    <v-row class="mt-4">
      <v-col cols="12" md="4">
        <v-card outlined class="pa-4" height="400">
          <v-card-title>Application Status</v-card-title>
          <pie-chart
            v-if="
              licensesStats?.[reviewUser.stage] &&
              licensesStats[reviewUser.stage].status
            "
            :chart-data="statusChartData"
            :options="chartOptions"
            :height="300"
          ></pie-chart>
          <div v-else class="text-center pa-5">No Data Available</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="pa-4" height="400">
          <v-card-title>Applications by Member Type</v-card-title>
          <bar-chart
            v-if="
              licensesStats?.[reviewUser.stage] &&
              licensesStats[reviewUser.stage].category
            "
            :chart-data="memberTypeChartData"
            :options="chartOptions"
            :height="300"
          ></bar-chart>
          <div v-else class="text-center pa-5">No Data Available</div>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card outlined class="pa-4" height="400">
          <v-card-title>Compliance Trend (Approvals)</v-card-title>
          <line-chart
            v-if="licensesMonthlyTrend"
            :chart-data="trendChartData"
            :options="chartOptions"
            :height="300"
          ></line-chart>
          <div v-else class="text-center pa-5">No Data Available</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Compliance & Operations -->
    <v-row class="mt-4">
      <v-col cols="12" md="6">
        <v-card outlined class="pa-4">
          <v-card-title>Turnaround Time (Avg Days)</v-card-title>
          <div class="text-center pa-10">
            <div class="text-h2 font-weight-bold primary--text">~1.2 Days</div>
            <div class="text-caption">
              Average time from submission to approval
            </div>
            <v-chip class="mt-3" color="green" dark small
              >Within Target (4 Days)</v-chip
            >
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card outlined class="pa-4">
          <v-card-title>Recent Audit Logs (Reviewer Actions)</v-card-title>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">License No</th>
                  <th class="text-left">Action/Stage</th>
                  <th class="text-left">Reviewer</th>
                  <th class="text-left">Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(log, i) in recentLogs" :key="i">
                  <td>{{ log.licenseNo }}</td>
                  <td>{{ log.toStage }}</td>
                  <td>{{ log.reviewerName }}</td>
                  <td>{{ formatDate(log.systemCreatedAt) }}</td>
                </tr>
                <tr v-if="recentLogs.length === 0">
                  <td colspan="4" class="text-center">No recent activity</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
      </v-col>
    </v-row>
    <LicenseReportsSelector v-model="reportDialog" />
  </v-container>
</template>

<script>
import BarChart from "@/modules/charts/BarChart";
import PieChart from "@/modules/charts/PieChart";
import LineChart from "@/modules/charts/LineChart";
import DateMixin from "@/mixins/DateMixin";
import moment from "moment";
import LicenseReportsSelector from "./LicenseReportsSelector.vue";
import { AuthService } from "@/modules/auth";

export default {
  name: "ExecutiveDashboard",
  components: { BarChart, PieChart, LineChart, LicenseReportsSelector },
  mixins: [DateMixin],
  data() {
    return {
      reportDialog: false,
      selectedYear: new Date().getFullYear(),
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
      },
    };
  },
  computed: {
    loading() {
      return this.$store.getters["License/loading"];
    },
    years() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years;
    },
    reviewUser() {
      return this.$store.getters["License/reviewUser"] || {};
    },
    licenses() {
      return this.$store.getters["License/licenses"] || [];
    },
    licensesMonthlyTrend() {
      return this.$store.getters["License/licenseMonthlyTrend"] || {};
    },
    licensesStats() {
      return this.$store.getters["License/licenseStats"] || {};
    },
    totalApplications() {
      return this.licensesStats?.[this.reviewUser?.stage]?.status?.All ?? 0;
    },
    approvedCount() {
      return (
        this.licensesStats?.[this.reviewUser?.stage]?.status?.Approved ?? 0
      );
    },
    pendingCount() {
      return this.licensesStats?.[this.reviewUser?.stage]?.status?.Pending ?? 0;
    },
    rejectedCount() {
      return (
        (this.licensesStats?.[this.reviewUser?.stage]?.status?.Declined ?? 0) +
        (this.licensesStats?.[this.reviewUser?.stage]?.status?.Onhold ?? 0)
      );
    },
    approvalRate() {
      if (this.totalApplications === 0) return 0;
      return ((this.approvedCount / this.totalApplications) * 100).toFixed(1);
    },
    sealedCount() {
      return this.licensesStats?.[this.reviewUser?.stage]?.status?.Approved;
    },
    statusChartData() {
      const statuses =
        this.licensesStats?.[this.reviewUser?.stage]?.status ?? {};
      delete statuses?.All;

      return {
        labels: Object.keys(statuses),
        datasets: [
          {
            backgroundColor: [
              "#4CAF50",
              "#FFC107",
              "#F44336",
              "#2196F3",
              "#9C27B0",
            ],
            data: Object.values(statuses),
          },
        ],
      };
    },
    memberTypeChartData() {
      const types =
        this.licensesStats?.[this.reviewUser?.stage]?.category ?? {};
      return {
        labels: Object.keys(types),
        datasets: [
          {
            label: "Applications by Category",
            backgroundColor: "#2196F3",
            data: Object.values(types),
          },
        ],
      };
    },
    trendChartData() {
      // Group approvals by month
      const trends = {};
      Object.keys(this.licensesMonthlyTrend).forEach((k) => {
        const month = moment(k.split("-")[0]).format("MMM YYYY");
        trends[month] = this.licensesMonthlyTrend[k];
      });

      return {
        labels: Object.keys(trends),
        datasets: [
          {
            label: "Monthly Approvals",
            borderColor: "#4CAF50",
            backgroundColor: "rgba(76, 175, 80, 0.2)",
            data: Object.values(trends),
            fill: true,
          },
        ],
      };
    },
    recentLogs() {
      // Aggregate all logs from all licenses
      const requestLogs =
        this.$store.getters["License/licenseReviewLogs"] ?? [];
      let allLogs = [];

      requestLogs.forEach((log) => {
        allLogs.push({
          licenseNo: log.serviceNo,
          toStage: log.toStage ? log.toStage : "Approved",
          reviewerName: log?.membershipLicenseApplication?.reviewerEntity
            ? log?.membershipLicenseApplication?.reviewerEntity?.names
            : "System",
          systemCreatedAt: log?.systemCreatedAt,
        });
      });

      return allLogs;
    },
    recentReturns() {
      return this.licenses
        .filter((l) => ["Rejected", "Declined", "Onhold"].includes(l?.status))
        .sort(
          (a, b) => new Date(b?.applicationDate) - new Date(a?.applicationDate)
        )
        .slice(0, 5);
    },
  },
  methods: {
    getStatusColor(status) {
      return (
        {
          Rejected: "error",
          Declined: "error",
          Onhold: "warning",
        }[status] || "grey"
      );
    },
    openReportDialog() {
      this.reportDialog = true;
    },
    fetchStats() {
      this.$store.dispatch("License/getLicenseStats", {
        documentType: "License",
        fromDate: `${this.selectedYear}-01-01`,
        reviewer: "",
        toDate: `${this.selectedYear}-12-31`,
      });
    },

    fetchMonthlyTrend() {
      this.$store.dispatch("License/getLicenseMonthlyTrend", {
        status: "",
      });
    },
    fetchReviewStageUser() {
      this.$store.dispatch("License/getReviewStageUser", {
        $filter: `documentType eq 'License' and Reviewer eq '${AuthService.user.code}'`,
      });
    },
  },
  mounted() {
    this.fetchStats();
    this.fetchReviewStageUser();
    this.fetchMonthlyTrend();

    this.$store.dispatch("License/getLicenseReviewLogs", {
      $top: 10,
      $filter: `serviceType eq 'License'`,
      $expand: `membershipLicenseApplication($expand=reviewerEntity)`,
    });
  },
};
</script>

<style scoped></style>
